import { useState } from 'react';
import { useJobs } from '../lib/queries';
import { useClearReviewFlag } from '../lib/mutations';
import { Button, EmptyState } from '../components/ui';
import { EditJobDialog } from '../board/EditJobDialog';
import type { JobWithContext } from '../lib/database.types';
import { cn, formatTimestamp } from '../lib/utils';

export function ReviewQueuePage() {
    const { data: jobs, isLoading } = useJobs({ needsReview: true });
    const clearMut = useClearReviewFlag();
    const [editJob, setEditJob] = useState<JobWithContext | null>(null);
    const [error, setError] = useState<string | null>(null);

    async function handleClear(jobId: string) {
        setError(null);
        try {
            await clearMut.mutateAsync(jobId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to clear flag');
        }
    }

    return (
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <div className="mb-6">
                <div className="flex items-baseline gap-3 mb-1">
                    <h1 className="font-display font-bold text-2xl text-graphite-900 dark:text-graphite-50">Review queue</h1>
                    <span className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                        {jobs?.length ?? 0} flagged
                    </span>
                </div>
                {/* Copy updated Aug 2026. The original text described import-era
                    flags only. Since the PO sync guard shipped, most flags are
                    jobs that came back on a later purchase order after someone
                    removed them from the board, which is a different situation
                    and needs different instructions. Each row carries its own
                    reason, so the heading stays general on purpose. */}
                <p className="text-sm text-graphite-600 dark:text-graphite-400">
                    Jobs that need a second look before anyone schedules them. Each one says why below.
                    Open it and fix whatever is wrong, or delete it if it does not belong to PPC, then
                    clear the flag to take it off this list.
                </p>
            </div>

            {error ? (
                <div className="mb-4 border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 rounded font-mono text-sm">
                    {error}
                </div>
            ) : null}

            {isLoading ? (
                <div className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                    Loading...
                </div>
            ) : !jobs || jobs.length === 0 ? (
                <EmptyState title="Queue is clear" description="No jobs currently flagged for review." />
            ) : (
                <div className="space-y-2">
                    {jobs.map((job) => (
                        <div
                            key={job.id}
                            className={cn(
                                'border border-amber-500/30 bg-amber-500/[0.04] rounded px-4 py-3.5',
                                'flex items-start gap-4'
                            )}
                        >
                            <div className="mt-0.5 shrink-0">
                                <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 mb-1">
                                    <span className="font-mono text-[11px] uppercase tracking-wider text-graphite-500">
                                        {job.builder.prefix}
                                    </span>
                                    <span className="font-display font-semibold text-graphite-900 dark:text-graphite-100 truncate">
                                        {job.community.name}
                                    </span>
                                    <span className="font-mono text-sm text-graphite-700 dark:text-graphite-300 tabular">
                                        Lot {job.lot_number}
                                    </span>
                                </div>
                                {job.review_reason ? (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 mb-1">{job.review_reason}</p>
                                ) : null}
                                {job.address ? (
                                    <div className="text-xs text-graphite-500">{job.address}</div>
                                ) : null}
                                {/* "Imported" was accurate when every flag came from the
                                    spreadsheet import. A job that returned on a purchase
                                    order was not imported, so the label is neutral now. */}
                                <div className="font-mono text-[10px] uppercase tracking-wider text-graphite-400 dark:text-graphite-600 mt-1">
                                    Added to board {formatTimestamp(job.created_at)}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1.5 shrink-0">
                                <Button variant="secondary" size="sm" onClick={() => setEditJob(job)}>
                                    Edit
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleClear(job.id)}
                                    disabled={clearMut.isPending}
                                >
                                    Clear flag
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editJob ? (
                <EditJobDialog open onClose={() => setEditJob(null)} job={editJob} />
            ) : null}
        </div>
    );
}
